import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./constant/LanguageContext";
import { ThemeProvider } from "./constant/theme";
import { NavigationProvider } from './constant/NavigationContext';
import { ModalProvider } from './constant/ModalContext';
import { sessionManager } from './utils/sessionManager';
import { MessengerProvider } from './context/MessengerContext';
import { NotificationProvider } from './context/NotificationContext';
import { MiniChatProvider } from './pages/(All) messenger/context/MiniChatContext';
import MiniChatDock from './pages/(All) messenger/components/MiniChatDock';
import MiniChatErrorBoundary from './pages/(All) messenger/components/MiniChatErrorBoundary';
import ClientBadge from "./components/ClientBadge";
import IdleSessionGuard from "./components/IdleSessionGuard";

// Main Pages
import LandingPage from "./pages/Landing/LandingPage";
import Login from "./pages/Login/LoginPage";
import Dashboard from "./pages/Admin_Acount/Admin_Acount";
import UserProfile from "./pages/User_Account";
import UserProfileAdmin from "./pages/(Admin) Profile";
import PasswordResetPage from './pages/Login/PasswordResetPage';
import ProfessionalDashboardManager from './pages/Manager_Account/index';
import Mo3tiUserProfilemanager from './pages/(Manager)user-profile/Mo3tiUserProfile';
import DashboardSuper from "./pages/(SuperAdmin)Profile";

// Role definitions
const ROLES = {
  ADMIN: "admin",
  MANAGER: "manager",
  USER: "user",
  SUPER: "superadmin"
};

// ✅ ENHANCED PrivateRoute with session management
function PrivateRoute({ children, allowedRoles = [] }) {
  const { user } = useAuth();

  // ✅ Check both session manager AND AuthContext
  const isAuthenticated = sessionManager.isAuthenticated();
  const userRole = sessionManager.getUserRole()?.toLowerCase();

  // ✅ If not authenticated in session manager, try to restore from storage
  if (!isAuthenticated) {
    // Try to get user from storage
    const storedUser = sessionManager.getUser();
    if (!storedUser) {
      return <Navigate to="/login" replace />;
    }
  }

  // ✅ Use userRole from session manager or user object as fallback
  const effectiveRole = userRole || user?.role?.toLowerCase();

  if (allowedRoles.length === 0) {
    return children;
  }

  const hasAccess = allowedRoles.some(role => role.toLowerCase() === effectiveRole);

  if (!hasAccess) {
    // ✅ Redirect to appropriate dashboard based on role
    if (effectiveRole === "user") {
      return <Navigate to="/userprofile" replace />;
    } else if (effectiveRole === "manager") {
      return <Navigate to="/DashboardManager" replace />;
    } else if (effectiveRole === "admin") {
      return <Navigate to="/dashboard" replace />;
    } else if (effectiveRole === "superadmin") {
      return <Navigate to="/DashboardSuper" replace />;
    }
    // If no role matches, redirect to login
    return <Navigate to="/login" replace />;
  }

  return children;
}

// ✅ ENHANCED Default Route Component
function DefaultRoute() {
  const { user } = useAuth();

  // ✅ Check both session manager AND user object
  const isAuthenticated = sessionManager.isAuthenticated();
  const userRole = (sessionManager.getUserRole() || user?.role)?.toLowerCase();

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Redirect based on role
  if (userRole === ROLES.ADMIN) {
    return <Navigate to="/dashboard/dashboard" replace />;
  } else if (userRole === ROLES.MANAGER) {
    return <Navigate to="/DashboardManager/dashboard" replace />;
  } else if (userRole === ROLES.USER) {
    return <Navigate to="/userprofile" replace />;
  } else if (userRole === ROLES.SUPER) {
    return <Navigate to="/DashboardSuper" replace />;
  }

  // Fallback to login if role is not recognized
  return <Navigate to="/login" replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <LanguageProvider>
          <MessengerProvider>
            {/* Router wraps NotificationProvider (not the reverse) so
                NotificationContext can call useLocation() to derive the
                current View for view-scoped notification fetching — see
                NotificationContext.js. Everything that was already inside
                NotificationProvider stays exactly where it was. */}
            <Router>
              <NotificationProvider>
                <ClientBadge />
                <IdleSessionGuard to="/login" idleTime={900000} warningTime={60000} />
                <MiniChatErrorBoundary>
                  <MiniChatProvider>
                    <MiniChatDock />
                  </MiniChatProvider>
                </MiniChatErrorBoundary>
                <ModalProvider>
                  <NavigationProvider>
                    <Routes>
                      {/* Public Routes */}
                      <Route path="/" element={<LandingPage />} />
                      <Route path="/login" element={<Login />} />
                      <Route path="/reset-password" element={<PasswordResetPage />} />

                      {/* Admin Dashboard - Only admins */}
                      <Route
                        path="/dashboard/*"
                        element={
                          <PrivateRoute allowedRoles={[ROLES.ADMIN]}>
                            <Dashboard />
                          </PrivateRoute>
                        }
                      />

                      {/* Manager Dashboard - Only managers */}
                      <Route
                        path="/DashboardManager/*"
                        element={
                          <PrivateRoute allowedRoles={[ROLES.MANAGER]}>
                            <ProfessionalDashboardManager />
                          </PrivateRoute>
                        }
                      />

                      {/* User Profile - Only regular users */}
                      <Route
                        path="/userprofile/*"
                        element={
                          <PrivateRoute allowedRoles={[ROLES.USER]}>
                            <UserProfile />
                          </PrivateRoute>
                        }
                      />

                      {/* Admin viewing employee profile */}
                      <Route
                        path="/userprofileadmin/*"
                        element={
                          <PrivateRoute allowedRoles={[ROLES.ADMIN]}>
                            <UserProfileAdmin />
                          </PrivateRoute>
                        }
                      />

                      {/* Manager viewing employee profile */}
                      <Route
                        path="/userprofilemanager/*"
                        element={
                          <PrivateRoute allowedRoles={[ROLES.MANAGER]}>
                            <Mo3tiUserProfilemanager />
                          </PrivateRoute>
                        }
                      />

                      <Route
                        path="/DashboardSuper/*"
                        element={
                          <PrivateRoute allowedRoles={[ROLES.SUPER]}>
                            <DashboardSuper />
                          </PrivateRoute>
                        }
                      />

                      {/* Catch-all route - Redirects to default route */}
                      <Route path="*" element={<DefaultRoute />} />
                    </Routes>
                  </NavigationProvider>
                </ModalProvider>
              </NotificationProvider>
            </Router>
          </MessengerProvider>
        </LanguageProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}